import { useCallback, useEffect, useState } from 'react'

// Types
import { CustomChangeEvent, Option } from 'common/types'
import {
  SubmitToEMRProps,
  submitToEmrValidationSchema,
} from './SubmitToEMR.types'

// Hooks
import useDecrementUsageMutation from 'lib/services/api/product-service/useUseItems'
import useFormValidation from 'lib/hooks/useFormValidation/useFormValidation'
import { useIsMobile } from 'assets/utils/mediaQueries'
import { useCompleteSurgeryTrays } from 'hooks/useCompleteSurgeryTrays'
import { useUser } from 'app/User'
import {
  useAssignTrayStatusAsCaseCompleteMutation,
  useGetScrubTechniciansQuery,
  useSetSurgeryStatusMutation,
  useUpdateSurgeryQuestionnaireMutation,
  useMigrateRepsToAuth0Mutation,
  useGetProceduresQuery,
} from 'lib/apollo/hooks'

// Utils
import { getUDIsWithQuantity } from 'lib/utils/data'
import { getExpiredAssetCount } from 'lib/utils/getExpiredAssetCount'

// Contexts
import { useRfSpongeCount } from 'lib/context/RfSpongeCountContext/RfSpongeCountContext'
import { useUndocumentedAssetsContext } from 'lib/context/UndocumentedAssetsContext'
import { useEditDocumentedListContext } from 'lib/context/EditDocumentedListContext/EditDocumentedListContext'
import {
  ORGANIZATIONS,
  useOrganizationSettings,
} from 'lib/context/OrganizationSettingsContext'

// Other
import { animateScroll as scroll } from 'react-scroll'
import { useFlags } from 'launchdarkly-react-client-sdk'
import dayjs from 'lib/dayjs'
import { enqueueSnackbar } from 'notistack'
import { useDateRange, useNotificationActions } from 'store/notifications'
import { loadNotifications } from 'lib/utils/loadNotifications'
import { useEnterpriseStore } from 'store'

export const useSubmitToEMRLogic = (props: SubmitToEMRProps) => {
  const { surgery, dtmHardwareAssetData, hasAssets, additionalNotes } = props
  const { user } = useUser()
  const flags = useFlags()
  const dateRange = useDateRange()
  const { refetch: refetchProcedures } = useGetProceduresQuery({})
  const { setNotifications } = useNotificationActions()
  const { showScrubTechnician } = useOrganizationSettings()
  const { questionnaire, triggerSubmit } = useEditDocumentedListContext()
  const { completeSurgeryTrays } = useCompleteSurgeryTrays()
  const orgId = user?.orgId
  const isAdventistUser = orgId === ORGANIZATIONS.adventist
  const isMobile = useIsMobile()
  const { assignTrayStatusAsCaseComplete } =
    useAssignTrayStatusAsCaseCompleteMutation()
  const [updateQuestionnaire] = useUpdateSurgeryQuestionnaireMutation()

  const [disableSubmit, setDisableSubmit] = useState(false)
  const [dtmTrayIds, setDTMTrayIds] = useState<number[]>([])
  const [openScrubTechModal, setOpenScrubTechModal] = useState(false)
  const [event, setEvent] = useState<any>()

  const [selectedScrubTechnician, setSelectedScrubTechnician] =
    useState<Option | null>(null)
  const [error, setError] = useState<string>()

  const [setSurgeryStatusSubmitted, setSurgeryStatusSubmittedMutation] =
    useSetSurgeryStatusMutation(surgery._id)

  const { mutateAsync: decrementUsage } = useDecrementUsageMutation()
  const [migrateRepsToAuth0] = useMigrateRepsToAuth0Mutation()
  const userName =
    user?.firstName || user?.lastName
      ? `${user?.firstName || 'Not'} ${user?.lastName || 'Set'}`
      : 'Not Set'

  const numberOfExpired = getExpiredAssetCount(surgery.assetGroups)

  const { rfSpongeAssetGroupCount } = useRfSpongeCount()

  const { undocumentedScanCount, undocumentedScans } =
    useUndocumentedAssetsContext()

  const { selectedDepartment, isDepartmentsLoaded } = useEnterpriseStore()
  const { data, loading, refetch } = useGetScrubTechniciansQuery(
    {
      departmentId: selectedDepartment?.id,
    },
    !isDepartmentsLoaded
  )

  const scrubTechs: Option[] =
    data?.getScrubTechnicians.data.map((scrubTechnician) => ({
      name: `${scrubTechnician.firstName} ${scrubTechnician.lastName} - ${scrubTechnician.speciality}`,
      id: scrubTechnician._id || '',
    })) || []

  useEffect(() => {
    const dtmTrayIdsArr = dtmHardwareAssetData
      ?.flatMap((asset) => asset.scans)
      ?.filter((scan) => scan.dtmScrewData)
      ?.map((scan) => Number(scan.dtmScrewData?.trayId))

    setDTMTrayIds(dtmTrayIdsArr)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undocumentedScans?.length])

  const handleUpdateQuestionnaire = useCallback(() => {
    updateQuestionnaire({
      variables: {
        surgeryId: surgery._id,
        questionnaire: questionnaire,
      },
    })
  }, [updateQuestionnaire, surgery._id, questionnaire])

  const handleSubmitRecord = useCallback(() => {
    handleUpdateQuestionnaire()
    setSurgeryStatusSubmitted({
      variables: {
        surgeryId: surgery._id,
        status: 'SUBMITTED',
        rep: userName,
        dateTime: dayjs().toISOString(),
        scrubTechnician: selectedScrubTechnician?.name,
        isEditingSubmittedSurgery: undefined,
        additionalNotes,
      },
    })
      .then(() => {
        const assetsToDecrement = getUDIsWithQuantity(surgery.assetGroups)
        decrementUsage({
          items: assetsToDecrement,
        })
        migrateRepsToAuth0({
          variables: {
            surgeryId: surgery._id,
          },
        })
          .then((response) => {
            const responseData = response.data?.migrateRepsToAuth0
            if (responseData?.data?.errors?.length) {
              enqueueSnackbar(responseData.data.errors[0], {
                variant: 'error',
              })
            }
          })
          .catch((error) => {
            console.error(error)
          })

        // Update notifications
        refetchProcedures({
          endDate: dateRange.before,
          startDate: dateRange.after,
          pendingOnly: true,
        })
          .then(({ data }) => {
            const notifications = loadNotifications(data)
            setNotifications(notifications)
          })
          .catch(() => {
            enqueueSnackbar('Failed to start procedure', { variant: 'error' })
          })
      })
      .finally(() => {
        scroll.scrollToTop()
      })
  }, [
    handleUpdateQuestionnaire,
    setSurgeryStatusSubmitted,
    surgery._id,
    surgery.assetGroups,
    userName,
    selectedScrubTechnician?.name,
    additionalNotes,
    decrementUsage,
    migrateRepsToAuth0,
    refetchProcedures,
    dateRange.before,
    dateRange.after,
    setNotifications,
  ])

  const {
    isFormValidated,
    isFormSubmitted,
    isSubmitDisabled,
    handleValidation,
    FormValidationAlertToasters,
  } = useFormValidation({
    validationSchema: submitToEmrValidationSchema,
  })

  const [isSubmittedToEmr, setIsSubmittedToEmr] = useState(false)

  const isAutopopulated = surgery?.autoPopulatedConsumables?.length > 0 || false

  useEffect(() => {
    const hasMissingSpongeCount = isAutopopulated
      ? false
      : rfSpongeAssetGroupCount.some((sponge) => {
          return (
            !sponge.accurateDeviceCount &&
            !sponge.deviceCountCorrectiveActionTaken
          )
        })

    setDisableSubmit(
      setSurgeryStatusSubmittedMutation.loading
        ? false
        : isSubmitDisabled || isFormSubmitted || hasMissingSpongeCount
    )
  }, [
    setSurgeryStatusSubmittedMutation.loading,
    isSubmitDisabled,
    isFormSubmitted,
    rfSpongeAssetGroupCount,
    isAutopopulated,
  ])

  const handleUpdateTrayAssignment = useCallback(async () => {
    if (dtmTrayIds.length === 0) {
      return
    }

    try {
      const trayId = dtmTrayIds[0]

      const assignResult = await assignTrayStatusAsCaseComplete(trayId)

      if (!assignResult) {
        throw new Error('Failed to assign tray status as case complete.')
      }
    } catch (error) {
      console.error(error)
    }
  }, [assignTrayStatusAsCaseComplete, dtmTrayIds])

  const handleSelectScrubTechnician = (e: CustomChangeEvent) => {
    setSelectedScrubTechnician(e.target.value as Option)
    setError(undefined)
  }

  const handleSubmitToEmr = async (e: any) => {
    e.preventDefault()

    const proceedWithSubmission = async () => {
      setSelectedScrubTechnician(null)
      if (showScrubTechnician) {
        setOpenScrubTechModal(true)
        setEvent(e)
      } else {
        handleValidation(e)
      }
    }
    if (hasAssets && flags.implantRecordQuestions) {
      const isValid = await triggerSubmit()
      if (isValid) {
        await completeSurgeryTrays(surgery._id)
        await proceedWithSubmission()
      }
    } else {
      await completeSurgeryTrays(surgery._id)
      await proceedWithSubmission()
    }
  }

  const handleCloseScrubTechModal = () => {
    setOpenScrubTechModal(false)
    setDisableSubmit(false)
    setIsSubmittedToEmr(false)
    setError(undefined)
  }

  const handleSubmitScrubTech = () => {
    if (!selectedScrubTechnician) {
      setError('Please select a scrub technician')
      return
    }

    setOpenScrubTechModal(false)
    handleValidation(event)
  }

  useEffect(() => {
    if (isFormSubmitted && isFormValidated && !isSubmittedToEmr) {
      handleUpdateTrayAssignment()
      handleSubmitRecord()
      setIsSubmittedToEmr(true)
    }
  }, [
    isFormSubmitted,
    isFormValidated,
    isSubmittedToEmr,
    handleSubmitRecord,
    handleUpdateTrayAssignment,
  ])

  return {
    error,
    loading,
    isMobile,
    scrubTechs,
    disableSubmit,
    isAdventistUser,
    numberOfExpired,
    openScrubTechModal,
    undocumentedScanCount,
    selectedScrubTechnician,
    setSurgeryStatusSubmittedMutation,
    refetch,
    handleSubmitToEmr,
    handleSubmitScrubTech,
    handleCloseScrubTechModal,
    handleSelectScrubTechnician,
    FormValidationAlertToasters,
    isEditingSubmittedSurgery:
      surgery?.status?.isEditingSubmittedSurgery || false,
  }
}
